import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as authApiModule from '@/services/authApi';
import { authApi } from '@/services/authApi';
import { GoogleAuthResponse, GoogleUser, signOutGoogle } from '@/services/googleAuthService';
// Note: dataHealthService and queryClient available for future prefetch functionality

export interface User {
  id: string;
  email: string;
  name: string;           // User's full name for display
  tenantId: string;       // Home tenant ID (immutable, for audit trail)
  tenantName?: string;    // Home tenant name
  role?: string;
  picture?: string;
  authProvider?: 'email' | 'google';
  profile?: string;
  // Agency admin fields (present when profile = 'agency-admin')
  agencyCode?: string;
  agencyLogoUrl?: string;    // Agency logo URL - populated at login, no separate API call needed
  agencyName?: string;       // Agency display name - populated at login
  allowedTenants?: { id: string; name: string }[];
  tenantConfirmed?: boolean; // true after explicitly selecting a tenant via TenantSwitcherScreen
  // Proxy fields (null before tenant switch, populated after)
  proxyUserId?: string;      // Acting-as user ID
  proxyTenantId?: string;    // Acting-as tenant ID (current context)
  proxyTenantName?: string;  // Acting-as tenant name
}

export interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isNewUser: boolean;
  passwordMustChange: boolean;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (authResponse: GoogleAuthResponse, googleUser: GoogleUser) => void;
  register: (payload: authApiModule.PublicRegisterRequest) => Promise<void>;
  logout: () => void;
  clearNewUserFlag: () => void;
  clearPasswordMustChange: () => void;
  updateCurrentTenantName: (tenantName: string) => void;
  /** Agency admin: re-issue JWT scoped to selected tenant; updates token + user in store. */
  switchTenant: (tenantId: string) => Promise<void>;
  /** Get effective tenant ID (proxy if switched, else home) */
  getEffectiveTenantId: () => string | undefined;
  /** Get effective tenant name (proxy if switched, else home) */
  getEffectiveTenantName: () => string | undefined;
}

const buildStoredUser = (
  userData: Partial<User> & Pick<User, 'id' | 'email' | 'name' | 'tenantId' | 'tenantName'> & {
    role?: string;
    profile?: string;
    agencyCode?: string;
    picture?: string;
    authProvider?: 'email' | 'google';
    agencyLogoUrl?: string;
    agencyName?: string;
    allowedTenants?: { id: string; name: string }[];
    tenantConfirmed?: boolean;
    proxyUserId?: string;
    proxyTenantId?: string;
    proxyTenantName?: string;
  }
): User => ({
  id: userData.id,
  email: userData.email,
  name: userData.name,
  tenantId: userData.tenantId,
  tenantName: userData.tenantName,
  role: userData.role,
  profile: userData.profile,
  agencyCode: userData.agencyCode,
  picture: userData.picture,
  authProvider: userData.authProvider ?? 'email',
  agencyLogoUrl: userData.agencyLogoUrl,
  agencyName: userData.agencyName,
  allowedTenants: userData.allowedTenants,
  tenantConfirmed: userData.tenantConfirmed ?? Boolean(userData.proxyTenantId),
  proxyUserId: userData.proxyUserId,
  proxyTenantId: userData.proxyTenantId,
  proxyTenantName: userData.proxyTenantName,
});

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isNewUser: false,
      passwordMustChange: false,
      _hasHydrated: false,
      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
      login: async (email: string, password: string) => {
        try {
          const { user, token, passwordMustChange, authProvider } = await authApiModule.login(email, password);
          localStorage.setItem('token', token);  // keep flat key for interceptor & other features
          set({
            user: buildStoredUser({
              ...user,
              authProvider: (authProvider as 'email' | 'google') || 'email',
              agencyLogoUrl: user.agencyLogoUrl,
              agencyName: user.agencyName,
              // Proxy fields are null on initial login, populated only after tenant switch
              proxyUserId: user.proxyUserId,
              proxyTenantId: user.proxyTenantId,
              proxyTenantName: user.proxyTenantName,
            }),
            token,
            isAuthenticated: true,
            isNewUser: false,
            passwordMustChange: passwordMustChange ?? false,
          });
          
          // Note: Health sources prefetch removed - now requires siteId parameter
          // Health data will be fetched when components mount with proper siteId context
        } catch (error) {
          console.error('Login failed:', error);
          throw new Error('Invalid credentials');
        }
      },
      loginWithGoogle: (authResponse: GoogleAuthResponse, googleUser: GoogleUser) => {
        console.log('✅ loginWithGoogle - storing user:', googleUser);
        const user = buildStoredUser({
          id: googleUser.id,
          email: googleUser.email,
          name: googleUser.name,
          tenantId: googleUser.tenantId,
          tenantName: googleUser.tenantName,  // Include organization name
          role: googleUser.role,
          profile: googleUser.profile,
          picture: googleUser.picture,
          authProvider: 'google',
        });
        localStorage.setItem('token', authResponse.accessToken);  // keep flat key for interceptor & other features
        set({
          user,
          token: authResponse.accessToken,
          isAuthenticated: true,
          isNewUser: authResponse.isNewUser,
        });
      },
      register: async (payload: authApiModule.PublicRegisterRequest) => {
        try {
          const response = await authApiModule.register(payload);
          if (!response.success) {
            throw new Error(response.message);
          }
          // Registration successful - user will login separately
          // Don't set auth state here, just return success
        } catch (error) {
          console.error('Registration failed:', error);
          throw new Error('Registration failed. Please try again.');
        }
      },
      logout: () => {
        // Sign out from Google if applicable
        signOutGoogle();
        localStorage.removeItem('token');  // clear flat key
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isNewUser: false,
          passwordMustChange: false,
        });
      },
      clearNewUserFlag: () => {
        set({ isNewUser: false });
      },
      clearPasswordMustChange: () => {
        set({ passwordMustChange: false });
      },
      updateCurrentTenantName: (tenantName: string) => {
        set((state) => ({
          user: state.user ? { ...state.user, tenantName } : state.user,
        }));
      },
      switchTenant: async (tenantId: string) => {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Not authenticated');
        const response = await authApi.post<import('../services/authApi').AuthResponse>(
          '/agency/switch-tenant',
          { tenantId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = response.data;
        const newToken = data.accessToken;
        
        localStorage.setItem('token', newToken);
        set((state) => ({
          token: newToken,
          user: state.user ? {
            ...state.user,
            // Note: tenantId (home) remains unchanged - preserved in backend
            // Proxy fields are populated by backend after tenant switch
            proxyUserId: data.proxyUserId,
            proxyTenantId: data.proxyTenantId,
            proxyTenantName: data.proxyTenantName,
            tenantConfirmed: true, // user has explicitly chosen a tenant
          } : null,
        }));
      },
      getEffectiveTenantId: () => {
        const { user } = useAuthStore.getState();
        return user?.proxyTenantId || user?.tenantId;
      },
      getEffectiveTenantName: () => {
        const { user } = useAuthStore.getState();
        return user?.proxyTenantName || user?.tenantName;
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
